import type { PageList } from '@omnia/ui'
import { getLocalStorage, NavigationService, UrlService } from '@omnia/ui'
import type { Film, MemberCompletedOrder, Showtime } from '@vista-digital/ocapi-types/v1'
import { observable } from 'mobx'
import { PagesEnum, type SubPagesType } from '@/shared/models/pages'
import { cmsDataService } from '@/shared/services/cms.data.service'
import { getRoutedUrl } from '../utils/getRoutedUrl'
import { isRouted } from '../utils/isRouted'
import { urlFriendly } from '../utils/urlFriendly'

class GenericNavigationService extends NavigationService<PagesEnum, SubPagesType> {
    getOverrideSiteIdPages(): Array<keyof PagesEnum> {
        return []
    }

    getPages(): PageList<PagesEnum, SubPagesType> {
        return [] as unknown as PageList<PagesEnum, SubPagesType>
    }
}

export class KinepolisUrlService extends UrlService<PagesEnum, SubPagesType> {
    protected filmDetailsPage: keyof PagesEnum = 'FilmDetails'
    protected cinemaPage: keyof PagesEnum = 'Cinemas'

    protected navigationService: NavigationService<PagesEnum, SubPagesType> = new GenericNavigationService(
        new PagesEnum()
    )

    public createLumosUrl(path: string): string {
        try {
            const cms = cmsDataService.get()
            const base = cms?.lumos?.baseUrl ?? ''
            const cmsCulture = String(cms?.culture ?? 'en-US')
            const langRoot = cmsCulture.toLowerCase() === 'fr-lu' ? 'fr-fr' : cmsCulture
            return `${base}/${langRoot}/${path}`
        } catch {
            return ''
        }
    }

    // Add custom methods here
    public getShowtimeUrl(showtime: Showtime): string {
        return this.createLumosUrl(`order/showtimes/${encodeURIComponent(showtime.id)}/seats`)
    }

    public getCompletedOrderUrl(order: MemberCompletedOrder): string {
        return this.createLumosUrl(`completed-order?orderId=${encodeURIComponent(order.id)}`)
    }

    public setNavigationService<S extends NavigationService<PagesEnum, SubPagesType>>(navigationService: S) {
        this.navigationService = navigationService
    }

    public getHomeUrlVersion(lang: string) {
        const homeUrlVersions = getLocalStorage('HomeUrlVersions') ?? {}
        const languageKey = Object.keys(homeUrlVersions).find(
            key => lang.toLowerCase().includes(key.toLowerCase()) || lang.toLowerCase() === key.toLowerCase()
        ) as keyof typeof homeUrlVersions
        return homeUrlVersions[languageKey] as string
    }

    private versions = observable.box<{ [key: string]: string }>(undefined)
    public setVersions(versions: { [key: string]: string }) {
        this.versions.set(versions)
    }

    public getVersion(lang: string) {
        const versions = this.versions.get()
        if (!versions) return null
        const languageKey = Object.keys(versions).find(key =>
            lang.toLowerCase().includes(key.toLowerCase())
        ) as keyof typeof versions
        return versions[languageKey]
    }

    public hasVersions() {
        return !!this.versions.get()
    }

    public urlFriendly(urlInput: string): string {
        return urlFriendly(urlInput)
    }

    public getFilmDetailsUrlForVersion(filmsPageUrl: string, filmName: string, filmId: string) {
        return `${filmsPageUrl}/${this.urlFriendly(filmName)}/${filmId}/`
    }

    public isRouted(url: string) {
        return isRouted(url)
    }

    public getRoutedUrl(url: string) {
        return getRoutedUrl(url)
    }

    public lastUrlSegment(url: string) {
        return url
            .split('/')
            .filter(i => i)
            .pop()
    }

    public removeLastUrlSegment(url: string) {
        if (url.endsWith('/')) {
            url = url.substring(0, url.length - 1)
        }

        return url.substring(0, url.lastIndexOf('/'))
    }

    public getCinemaUrl(cinemaName: string) {
        return this.navigationService.getPageUrl('Cinema', {
            cinemaName: cinemaName,
        })
    }

    public getFilmDetailsUrl(film: Film, siteId?: string, extraParams?: { [key: string]: string }): string {
        try {
            return super.getFilmDetailsUrl(film, siteId, extraParams)
        } catch {
            return ''
        }
    }
}

export const urlService = new KinepolisUrlService()
